import { Download, FileJson, Loader2, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useExportLayout } from '@/hooks/useAdminLayouts'
import { useExportLocation } from '@/hooks/useAdminLocations'

interface ExportJsonMenuProps {
  /** Present → offers "this layout + its location". */
  layoutId?: string
  /** Present → offers "location + all layouts". */
  locationId?: string
  size?: 'sm' | 'default'
  variant?: 'outline' | 'ghost' | 'default'
}

/**
 * Export dropdown shared by the layout detail, location detail and user detail
 * pages. Renders only the items whose id it was given, so the Locations card on
 * /users/:id shows a single-purpose location export while /layouts/:id shows both.
 *
 * Every download is audit-logged server-side — no reason prompt here.
 */
export function ExportJsonMenu({
  layoutId,
  locationId,
  size = 'sm',
  variant = 'outline',
}: ExportJsonMenuProps) {
  const exportLayout = useExportLayout()
  const exportLocation = useExportLocation()
  const isPending = exportLayout.isPending || exportLocation.isPending

  if (!layoutId && !locationId) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={isPending}>
          {isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Full DB rows incl. scene_data
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {layoutId && (
          <DropdownMenuItem onSelect={() => exportLayout.mutate(layoutId)}>
            <FileJson className="mr-2 h-4 w-4" /> This layout + location
          </DropdownMenuItem>
        )}
        {locationId && (
          <DropdownMenuItem onSelect={() => exportLocation.mutate({ locationId })}>
            <MapPin className="mr-2 h-4 w-4" /> Location + all layouts
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
